import { create } from 'zustand';
import {
  DEFAULT_TABLE_LIMIT,
  emptyTableQuery,
  type Sort,
  type TableFilter,
  type TableQuery,
  type ViewMode,
} from '../types';

// SC-2379 / SC-2376 — the canonical, per-catalog table-view state. The entity
// table's query intent (filters, cursor back-stack, projection) lived as
// scattered `useState` in EntitiesPage; hoisting it here makes ONE `TableQuery`
// the single source of truth that BOTH the user's controls AND the agent's
// `get_table_slice` directive write into — the thesis of the table-controller
// initiative. The search box is just one writer of the `text` filter; the agent
// (`applyDirective`) writes the whole filter list; future sort/facet/chip
// controls write here too.
//
// Keyed by `catalogId` (mirroring `agentChatStore`), so each catalog keeps its
// own filters + page: switching catalogs and back RESUMES where you left off
// rather than resetting. The persistent fields below survive a switch; the
// transient ones (`isPaging` / `pageError`) are cleared for the OUTGOING catalog
// on switch (see `clearTransient`) so a prefetch abandoned mid-switch can't strand
// a slice with `isPaging` stuck true.

export interface CatalogTableSlice {
  // --- Persistent across catalog switches (the "resume where you left off") --
  /** Raw search-box buffer. Debounced by EntitiesPage into the `text` filter
   *  within `filters` (`commitSearch`). Kept distinct from the committed `text`
   *  filter so catalog re-entry / directive-sync (which re-seed `search` from
   *  the slice) is a no-op for the debounce — it only commits when the raw
   *  buffer diverges from the committed text, so the resumed page isn't nuked. */
  search: string;
  /** SC-3527 — the column the search box is scoped to, or `null` for "All
   *  columns" (the catalog-wide `text` filter). `undefined` until the table first
   *  mounts for this catalog and `ensureSearchField` resolves the catalog's
   *  default (its ID column). The box owns ONE "lane" filter, chosen by this:
   *  the `text` filter when null, else a single `contains` on this field (see
   *  `searchLaneQuery`). */
  searchField?: string | null;
  /** The canonical AND-combined filter list — the single source of truth both
   *  the controls and the agent write into. The search box owns the one `text`
   *  filter; the agent's directive replaces / refines the whole list. */
  filters: TableFilter[];
  /** Column projection. `[]` ⇒ the catalog's preview_fields. The agent's
   *  directive can set this; controls leave it `[]` for now. */
  columns: string[];
  /** Active column sort (SC-2409), or null for the default stable `_id` order.
   *  `key` is a real column, the identity field, or the synthetic `_completeness`
   *  (Health). Both the user (header click) and the agent (`get_table_slice`
   *  directive) write it, so the header indicator reflects either producer. */
  sort: Sort | null;
  /** Page size POSTed to `/table` (SC-2409). Defaults to `DEFAULT_TABLE_LIMIT`; an
   *  agent directive can shrink it for a top-N ("the 5 most expensive"). Every
   *  user control (search / facets / header sort / clear) resets it to the
   *  default, so a deliberate control exits an agent-set cap. */
  limit: number;
  /** User-facing caption when an agent directive drove the current view, else
   *  null. Persists (paging/searching within the view keeps it) until the user
   *  clears the view or a new directive arrives — a provenance + escape marker. */
  narration: string | null;
  /** SC-2637 — cross-view provenance: set when the current `identifier` filter came
   *  from a Findings-view "Applies to" click (the table is scoped to one finding's
   *  entities). Drives the finding-provenance banner (Back to finding / Clear) and
   *  suppresses the pinned-rows pill. Persists like `narration` (paging/searching
   *  within the lens keeps it), but is cleared the moment the identifier filter is
   *  dropped — via Clear (`clearView`), a facet edit that removes it (`setFilters`),
   *  or a new provenance owner taking over (`applyDirective`). The affected count +
   *  ids live in the filter itself, so the banner can't drift from the filter. */
  fromFinding: { findingId: string; summary: string } | null;
  /** Cursor back-stack: `cursors[i]` is the opaque cursor for page `i`
   *  (`cursors[0]` = null = first page); forward cursors are discovered from
   *  each page's `next_cursor`. `pageIdx` is the 0-based current page. */
  cursors: (string | null)[];
  pageIdx: number;
  // --- Transient (cleared for the outgoing catalog on switch) ----------------
  /** Failed-forward-page banner (confirmed pagination keeps the good page
   *  underneath). */
  pageError: string | null;
  /** True during an in-flight forward prefetch — disables the pager. */
  isPaging: boolean;
}

export const EMPTY_TABLE_SLICE: CatalogTableSlice = {
  search: '',
  filters: [],
  columns: [],
  sort: null,
  limit: DEFAULT_TABLE_LIMIT,
  narration: null,
  fromFinding: null,
  cursors: [null],
  pageIdx: 0,
  pageError: null,
  isPaging: false,
};

/** The query of the `text` filter in a filter list (the search box's lane), or
 *  `''` when none. Always trimmed (that's how `commitSearch` stores it). */
export function textFilterQuery(filters: TableFilter[]): string {
  const f = filters.find((x) => x.type === 'text');
  return f && f.type === 'text' ? f.query : '';
}

// SC-2400 — the pinned-rows control's lane. Ranked/semantic asks ("top 5 worst")
// arrive as an `identifier` filter: the agent decomposes via
// `get_finding_hotspots` and pins the worst entity ids. There's no facet or
// search box for "these specific rows", so the canonical filter is marked by a
// removable pill. Both helpers are pure + exported (mirroring `textFilterQuery`)
// so the control's derived state is unit-testable without a store/DOM.

/** All pinned entity identifiers across any `identifier` filters, in order. */
export function pinnedIdentifiers(filters: TableFilter[]): string[] {
  return filters.flatMap((f) => (f.type === 'identifier' ? f.ids : []));
}

/** Drop every `identifier` filter — the pinned-rows pill's "Clear". Surgical (it
 *  leaves the search box, the rail filters, AND the agent-view caption intact),
 *  unlike `clearView` which resets the whole view. */
export function clearIdentifierFilters(filters: TableFilter[]): TableFilter[] {
  return filters.filter((f) => f.type !== 'identifier');
}

/** Upsert the single `text` filter into a list, keeping all other (e.g.
 *  agent-supplied) filters. An empty/whitespace query removes it. */
function withTextFilter(filters: TableFilter[], query: string): TableFilter[] {
  return withSearchLane(filters, null, query);
}

// SC-3527 — the search box's lane. Scoped to a column, the box owns one
// `field_value` `contains` filter on that column instead of the catalog-wide
// `text` filter. "One" is what makes it the box's: a `contains` with several
// values, or a different op, is somebody else's predicate and is left alone.

/** Whether `f` is the search lane's filter for `field` (`null` = All columns). */
function isSearchLaneFilter(f: TableFilter, field: string | null): boolean {
  if (field === null) return f.type === 'text';
  return (
    f.type === 'field_value' && f.op === 'contains' && f.field === field && f.values?.length === 1
  );
}

/** The committed query in the search lane for `field` (`null` = the `text`
 *  filter), or `''` when there is none. */
export function searchLaneQuery(filters: TableFilter[], field: string | null): string {
  const f = filters.find((x) => isSearchLaneFilter(x, field));
  if (!f) return '';
  if (f.type === 'text') return f.query;
  return f.type === 'field_value' ? String(f.values?.[0] ?? '') : '';
}

/** Upsert the search lane's filter for `field`, keeping every other filter. An
 *  empty/whitespace query removes it. */
export function withSearchLane(
  filters: TableFilter[],
  field: string | null,
  query: string,
): TableFilter[] {
  const others = filters.filter((x) => !isSearchLaneFilter(x, field));
  const trimmed = query.trim();
  if (!trimmed) return others;
  const lane: TableFilter =
    field === null
      ? { type: 'text', query: trimmed }
      : { type: 'field_value', field, op: 'contains', values: [trimmed] };
  return [...others, lane];
}

/** The column a catalog's search box defaults to: the field people call an
 *  entity by (`display_order`), else its row key (`identifier_fields`), as long
 *  as it's a live column. `null` (All columns) for an unprofiled catalog. */
export function defaultSearchField(catalog: {
  field_names: string[];
  display_order: string[];
  identifier_fields: string[];
}): string | null {
  const live = new Set(catalog.field_names);
  return [...catalog.display_order, ...catalog.identifier_fields].find((f) => live.has(f)) ?? null;
}

/** AND a directive's `incoming` filters into the `current` list (the `refine`
 *  mode). The `text` filter is replaced (only one makes sense); other filters
 *  append unless an identical one is already present. */
function mergeFilters(current: TableFilter[], incoming: TableFilter[]): TableFilter[] {
  let result = [...current];
  for (const f of incoming) {
    if (f.type === 'text') {
      result = withTextFilter(result, f.query);
    } else if (!result.some((x) => JSON.stringify(x) === JSON.stringify(f))) {
      result.push(f);
    }
  }
  return result;
}

interface TableViewStore {
  byCatalog: Record<string, CatalogTableSlice>;
  /** Update the raw search buffer (every keystroke). Does NOT touch the
   *  committed filters or pagination — that's `commitSearch` after the debounce. */
  setSearch: (catalogId: string, search: string) => void;
  /** Commit the debounced search → upsert the `text` filter (keeping any
   *  agent-supplied filters). A new filter set is a new result page, so reset to
   *  the first page and clear the transient paging flags (this also clears the
   *  flag of any forward prefetch the commit abandons). Leaves `narration` — the
   *  user searching within an agent view keeps the caption + its escape. */
  commitSearch: (catalogId: string, search: string) => void;
  /** SC-3527 — the user picked a search scope. Moves what's in the box from the
   *  old lane to the new one (All columns ↔ a column, or column → column), so the
   *  box keeps searching for what it shows. A new filter set is a new result
   *  page — same reset as `commitSearch`. */
  setSearchField: (catalogId: string, field: string | null) => void;
  /** SC-3527 — resolve the search scope when the table mounts for a catalog. A
   *  no-op once a live scope is set. Otherwise (first visit, or the chosen column
   *  is gone) it picks `defaultField` — or All columns when the filters already
   *  hold a `text` filter, so the box shows it — and re-seeds the box buffer from
   *  that lane. Never touches `filters`. */
  ensureSearchField: (catalogId: string, defaultField: string | null, fieldNames: string[]) => void;
  /** Set the whole canonical filter list (the facet rail's lane — SC-2395). The
   *  rail computes the next list with the pure `facetFilters` helpers and hands
   *  it here; the store stays agnostic of facet semantics. A new filter set is a
   *  new result page, so reset to the first page + clear the transient paging
   *  flags (mirrors `commitSearch`). Leaves `search` (the rail never touches the
   *  `text` filter, so the raw buffer stays in sync — and syncing it here could
   *  clobber an in-progress, not-yet-debounced keystroke) and `narration` (a
   *  facet refinement within an agent view keeps the caption + its escape). */
  setFilters: (catalogId: string, filters: TableFilter[]) => void;
  /** Set the active column sort (the header-click lane — SC-2409). A new order is
   *  a new keyset walk, so reset to the first page + clear the transient paging
   *  flags (mirrors `setFilters`), and reset `limit` to the default — a deliberate
   *  re-sort exits any agent top-N. `null` clears back to the default `_id` order.
   *  Leaves `search` + `narration` (re-sorting within a view keeps the box +
   *  caption). */
  setSort: (catalogId: string, sort: Sort | null) => void;
  /** Set the column projection (the column-manager's lane — SC-2412). Unlike
   *  `setFilters`/`commitSearch`, this KEEPS the current page: the projection is
   *  cursor-independent (the cursor is filter-scoped), so showing/hiding/reordering
   *  a column re-fetches the SAME page with a different column set rather than
   *  jumping back to page 1. `[]` ⇒ the catalog's preview_fields (the default).
   *  Leaves `search` + `narration` (editing columns within an agent view keeps
   *  both). */
  setColumns: (catalogId: string, columns: string[]) => void;
  /** Apply an agent `get_table_slice` directive. `replace` swaps the whole view
   *  to `query`; `refine` ANDs `query.filters` into the current filters. Either
   *  way: sync the search box to the resulting `text` filter, stash the
   *  narration caption, and reset to the first page. */
  applyDirective: (
    catalogId: string,
    query: TableQuery,
    mode: ViewMode,
    narration: string | null,
  ) => void;
  /** SC-2637 — scope the table to ONE finding's entities from the Findings view's
   *  "Applies to" link. Replaces the whole view with a single `identifier` filter (a
   *  focused lens — same reset as `clearView`, plus that one filter) and stamps
   *  `fromFinding` provenance so the table shows the finding-provenance banner
   *  instead of the pinned-rows pill. Clears any agent `narration` — this is a new,
   *  user-driven provenance owner. Reset to the first page, default columns/sort. */
  applyFindingScope: (
    catalogId: string,
    scope: { findingId: string; summary: string; entityIds: string[] },
  ) => void;
  /** Drop the current view back to the default unfiltered first page (the
   *  caption's "Clear" affordance, and the escape from an agent view). */
  clearView: (catalogId: string) => void;
  /** Confirmed-pagination success commit: record the next page's cursor and
   *  advance. Trims any forward cursors past the page we advanced FROM (a stale
   *  forward branch is void once we re-page). */
  commitNextPage: (catalogId: string, fromPageIdx: number, cursor: string) => void;
  /** Step back to an already-visited (cached) page; clears the page banner. */
  pageBack: (catalogId: string) => void;
  setPaging: (catalogId: string, isPaging: boolean) => void;
  setPageError: (catalogId: string, pageError: string | null) => void;
  /** Send the view back to its FIRST page without touching the filters.
   *
   *  For a write that changes which rows the current filters match — a bulk
   *  channel membership change while a `destination` filter is active (SC-3147).
   *  Invalidating the query refetches the CURRENT cursor, and a cursor is only
   *  meaningful against the result set it came from: once rows leave, it can
   *  point past the end, so the user lands on an empty page with a nonsense
   *  "Showing 51–50 of 50" label and no way to tell what happened.
   *
   *  Deliberately narrower than `setFilters` (which also resets `limit` and can
   *  drop the finding provenance) and than `clearView` (which drops the filters
   *  themselves) — the filters are still exactly what the user asked for; only
   *  their pagination is stale. */
  resetPaging: (catalogId: string) => void;
  /** Clear the transient flags for a catalog being switched AWAY from (or on
   *  table-page unmount), so an abandoned forward prefetch can't leave its slice
   *  with `isPaging` stuck true, and a stale error banner doesn't resurface on
   *  return. Leaves the persistent fields (filters / search / cursors / pageIdx /
   *  narration) intact — that's the resume state. */
  clearTransient: (catalogId: string) => void;
  /** Wipe ALL per-catalog table-view state. Called on an identity change
   *  (logout / login) via `clearSessionState` so the next account can't render
   *  the previous one's filters / search / cursors / open drawer (SC-2461). */
  reset: () => void;
}

function getSlice(state: TableViewStore, catalogId: string): CatalogTableSlice {
  return state.byCatalog[catalogId] ?? EMPTY_TABLE_SLICE;
}

function withSlice(
  state: TableViewStore,
  catalogId: string,
  patch: Partial<CatalogTableSlice>,
): Pick<TableViewStore, 'byCatalog'> {
  return {
    byCatalog: {
      ...state.byCatalog,
      [catalogId]: { ...getSlice(state, catalogId), ...patch },
    },
  };
}

/**
 * Derive the `TableQuery` POSTed to `/table` from the (persistent) slice state.
 * Pure + exported so the query model is unit-testable without a store/react
 * harness, and so the agent producer's directive round-trips the same shape.
 * `sort` rides the slice's active sort (SC-2409 — null is the default `_id`
 * order); `limit` rides the slice's page size (an agent top-N can shrink it).
 */
export function buildTableQuery(
  slice: Pick<CatalogTableSlice, 'filters' | 'columns' | 'sort' | 'limit' | 'cursors' | 'pageIdx'>,
): TableQuery {
  return emptyTableQuery({
    filters: slice.filters,
    columns: slice.columns,
    sort: slice.sort,
    limit: slice.limit,
    cursor: slice.cursors[slice.pageIdx] ?? null,
  });
}

export const useTableViewStore = create<TableViewStore>((set) => ({
  byCatalog: {},

  setSearch: (catalogId, search) =>
    set((state) => withSlice(state, catalogId, { search })),

  commitSearch: (catalogId, search) =>
    set((state) =>
      withSlice(state, catalogId, {
        search,
        filters: withSearchLane(
          getSlice(state, catalogId).filters,
          getSlice(state, catalogId).searchField ?? null,
          search,
        ),
        limit: DEFAULT_TABLE_LIMIT,
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      }),
    ),

  setSearchField: (catalogId, field) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      const from = slice.searchField ?? null;
      if (from === field) return state;
      // Move what's typed — the raw buffer, so a keystroke still inside the
      // debounce window isn't lost — out of the old lane and into the new one.
      // With nothing typed there is nothing to move: the box adopts whatever the
      // new lane already holds (an agent's `contains` on that column) and shows
      // it, rather than deleting a filter the user never saw.
      const typed = slice.search.trim();
      const cleared = withSearchLane(slice.filters, from, '');
      const filters = typed ? withSearchLane(cleared, field, typed) : cleared;
      return withSlice(state, catalogId, {
        searchField: field,
        search: typed ? slice.search : searchLaneQuery(cleared, field),
        filters,
        limit: DEFAULT_TABLE_LIMIT,
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      });
    }),

  ensureSearchField: (catalogId, defaultField, fieldNames) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      const current = slice.searchField;
      if (current === null || (current !== undefined && fieldNames.includes(current))) {
        return state;
      }
      const field = slice.filters.some((f) => f.type === 'text') ? null : defaultField;
      return withSlice(state, catalogId, {
        searchField: field,
        // Re-seed the buffer from the lane the box now shows, so the debounce
        // sees no divergence and can't commit over an agent-set filter.
        search: searchLaneQuery(slice.filters, field),
      });
    }),

  setFilters: (catalogId, filters) =>
    set((state) => {
      // SC-2637 — the finding-provenance banner is tied to the identifier filter: a
      // facet edit (or any whole-list replacement) that removes it clears the banner
      // too. Keep the provenance only while an identifier filter still governs.
      const keepProvenance = filters.some((f) => f.type === 'identifier');
      return withSlice(state, catalogId, {
        filters,
        fromFinding: keepProvenance ? getSlice(state, catalogId).fromFinding : null,
        limit: DEFAULT_TABLE_LIMIT,
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      });
    }),

  setSort: (catalogId, sort) =>
    set((state) =>
      withSlice(state, catalogId, {
        sort,
        limit: DEFAULT_TABLE_LIMIT,
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      }),
    ),

  setColumns: (catalogId, columns) =>
    // Keep the page (projection is cursor-independent), but clear a stale
    // forward-page error banner like every other view-change action — the column
    // change refetches the current page successfully, so a leftover "failed to
    // load page N+1" banner no longer applies (and has no other dismiss path).
    set((state) => withSlice(state, catalogId, { columns, pageError: null })),

  applyDirective: (catalogId, query, mode, narration) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      const filters =
        mode === 'refine' ? mergeFilters(slice.filters, query.filters) : query.filters;
      // refine keeps the current projection unless the directive sets one;
      // replace takes the directive's projection wholesale.
      const columns =
        mode === 'refine' ? (query.columns.length ? query.columns : slice.columns) : query.columns;
      // `replace` takes the directive's ordering + cap wholesale (a fresh view).
      // `refine` is a narrowing of what's already on screen, so it preserves the
      // current order/cap unless the directive re-sorts (`query.sort ?? slice.sort`,
      // symmetric with `columns`). `limit` is always present (defaults 50), so a
      // refine can't tell an explicit 50 from the default — it keeps the prior cap.
      // The `?? DEFAULT_TABLE_LIMIT` is defensive: the directive crosses the SSE
      // `unknown` boundary and `isViewDirective` doesn't validate `limit`, so a
      // payload missing it must not strand `slice.limit` as undefined (→ NaN pager
      // math); a malformed `sort` still 422s at POST, the established directive path.
      const sort = mode === 'refine' ? (query.sort ?? slice.sort) : (query.sort ?? null);
      const limit = mode === 'refine' ? slice.limit : (query.limit ?? DEFAULT_TABLE_LIMIT);
      // Sync the box to the lane it shows (SC-3527). A directive that brings a
      // `text` filter flips the box to All columns, or the box would hide it.
      // An unresolved scope stays unresolved: `ensureSearchField` seeds it.
      const searchField = filters.some((f) => f.type === 'text') ? null : slice.searchField;
      return withSlice(state, catalogId, {
        filters,
        columns,
        sort,
        limit,
        narration,
        // An agent directive is a new provenance owner — it supersedes any
        // finding scope (whose banner would otherwise linger over the new view).
        fromFinding: null,
        searchField,
        search: searchLaneQuery(filters, searchField ?? null),
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      });
    }),

  applyFindingScope: (catalogId, { findingId, summary, entityIds }) =>
    set((state) =>
      withSlice(state, catalogId, {
        search: '',
        filters: [{ type: 'identifier', ids: entityIds }],
        columns: [],
        sort: null,
        limit: DEFAULT_TABLE_LIMIT,
        narration: null,
        fromFinding: { findingId, summary },
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      }),
    ),

  clearView: (catalogId) =>
    set((state) =>
      withSlice(state, catalogId, {
        search: '',
        // Back to the catalog's default scope too (re-resolved on next mount).
        searchField: undefined,
        filters: [],
        columns: [],
        sort: null,
        limit: DEFAULT_TABLE_LIMIT,
        narration: null,
        fromFinding: null,
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      }),
    ),

  commitNextPage: (catalogId, fromPageIdx, cursor) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      const cursors = slice.cursors.slice(0, fromPageIdx + 1);
      cursors[fromPageIdx + 1] = cursor;
      return withSlice(state, catalogId, { cursors, pageIdx: fromPageIdx + 1 });
    }),

  pageBack: (catalogId) =>
    set((state) => {
      const slice = getSlice(state, catalogId);
      return withSlice(state, catalogId, {
        pageIdx: Math.max(0, slice.pageIdx - 1),
        pageError: null,
      });
    }),

  setPaging: (catalogId, isPaging) =>
    set((state) => withSlice(state, catalogId, { isPaging })),

  setPageError: (catalogId, pageError) =>
    set((state) => withSlice(state, catalogId, { pageError })),

  resetPaging: (catalogId) =>
    set((state) =>
      withSlice(state, catalogId, {
        cursors: [null],
        pageIdx: 0,
        pageError: null,
        isPaging: false,
      }),
    ),

  clearTransient: (catalogId) =>
    set((state) =>
      withSlice(state, catalogId, {
        isPaging: false,
        pageError: null,
      }),
    ),

  reset: () => set({ byCatalog: {} }),
}));
